import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'
import routes from './routes'
import authStorageService from '../services/auth/auth-storage.service.js';

Vue.use(VueRouter)
Vue.use(VueMeta, { 
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'hash',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

router.beforeEach((routeTo, routeFrom, next) => {

  // Developer docs routes are always public
  if (routeTo.path.startsWith('/developer-docs')) {
    return next();
  }

  const publicPages = ['/login', '/register', '/forgot-password', '/password-reset'];
  const authpage = !publicPages.includes(routeTo.path);
  
  // Use synchronous authentication check
  const loggeduser = authStorageService.isAuthenticated();

  // Handle unauthenticated users trying to access protected pages
  if (authpage && !loggeduser && !routeTo.path.toString().includes('verify')) {
    return next('/login');
  }
 
  const staffProfile = authStorageService.getStaffProfile();
  const user = authStorageService.getUser();

  // Get the user role from the appropriate source
  let userRole = null;
  if (staffProfile && typeof staffProfile.userRole === 'string') {
    userRole = staffProfile.userRole.trim();
  } else if (user && typeof user.userRole === 'string') {
    userRole = user.userRole.trim().toUpperCase(); // Ensure it matches the AccessLevels format
  } else if (user && typeof user.userType === 'string') {
    // Fallback to userType if userRole is not available
    const userType = user.userType.trim();
    // Map userType to userRole format
    switch(userType) {
      case 'branchStaff': userRole = 'BRANCH_STAFF'; break;
      case 'branchAdmin': userRole = 'BRANCH_ADMIN'; break;
      case 'hqStaff': userRole = 'HQ_STAFF'; break;
      case 'superAdminDefault': userRole = 'SUPER_ADMIN'; break;
      default: userRole = null;
    }
  }

  if (routeTo.meta.authRequired) {
    // Check if the userRole exists and if it is included in the allowed roles
    if (!routeTo.meta.roles || !routeTo.meta.roles.includes(userRole)) { 
       return next('/unauthorized'); // Redirect to unauthorized page
    }
  }
  next();
});


// Handle chunk load errors (happens after deployment when browser has cached old chunks)
router.onError((error) => {
  const isChunkLoadError = error.name === 'ChunkLoadError' || 
    (error.message && error.message.includes('Loading chunk')) ||
    (error.message && error.message.includes('Loading CSS chunk'));
  
  if (isChunkLoadError) {
    console.warn('Chunk load error detected. Reloading page to get fresh assets...');
    // Clear any service worker cache if present
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }
    // Force reload from server (not from cache)
    window.location.reload(true);
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Handle beforeResolve hooks defined in route meta
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      if (route.meta && route.meta.beforeResolve) {
        // Create a promise to handle the beforeResolve hook
        const shouldContinue = await new Promise((resolve) => {
          route.meta.beforeResolve(routeTo, routeFrom, (redirectTo) => {
            if (redirectTo) {
              // If there's a redirect, handle it and stop processing
              next(redirectTo);
              resolve(false); // Don't continue processing
            } else {
              resolve(true); // Continue processing
            }
          });
        });
        
        // If a redirect occurred, stop processing
        if (!shouldContinue) {
          return;
        }
      }
    }
  } catch (error) {
    // Handle chunk load errors during navigation
    const isChunkLoadError = error.name === 'ChunkLoadError' || 
      (error.message && error.message.includes('Loading chunk'));
    
    if (isChunkLoadError) {
      console.warn('Chunk load error during navigation. Reloading...');
      window.location.reload(true);
      return;
    }
    
    console.error('Router beforeResolve error:', error);
    next();
    return;
  }

  // If we reach this point, continue resolving the route
  next();
})

export default router
